/**
 * Pinning state for Reports workspace. Persists pinned report template IDs in localStorage.
 */

import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'nb:reports:pinnedIds';

function loadPinnedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function savePinnedIds(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* noop */
  }
}

export function useReportPins() {
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => loadPinnedIds());

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const togglePin = useCallback((reportId: string) => {
    setPinnedIds((prev) => {
      const next = prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId];
      savePinnedIds(next);
      return next;
    });
  }, []);

  const isPinned = useCallback(
    (reportId: string) => pinnedSet.has(reportId),
    [pinnedSet]
  );

  return { pinnedIds, pinnedSet, togglePin, isPinned };
}
